import { useTranslation } from "react-i18next"

import { FileDown } from "@/components/icons"
import { buttonVariants } from "@/components/ui/button-variants"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useCitationFileDownload } from "@/hooks/use-citation-file"
import { logger } from "@/lib/logger"
import { cn } from "@/lib/utils"
import { extractCleanFilename } from "@/utils/file-utils"

type DownloadButtonProps = {
  fileUrl: string
  title?: string | null
  /** Override default download tooltip (e.g. "Original file") */
  tooltip?: string
}

export function DownloadButton({ fileUrl, title = null, tooltip }: DownloadButtonProps) {
  const { t } = useTranslation()
  const isUrlValid = fileUrl.includes("/api/content/") || fileUrl.includes("/api/session-content/")
  const downloadMutation = useCitationFileDownload()

  if (!isUrlValid) {
    return <></>
  }

  const handleDownload = async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault()

    try {
      logger.debug(`Downloading file: ${fileUrl}`)

      // Use React Query mutation to download file
      const blob = await downloadMutation.mutateAsync(fileUrl)

      // Extract filename from URL or use provided title
      let filename = title || ""

      if (!filename) {
        filename = extractCleanFilename(fileUrl, "download")
        logger.debug(`Using extracted filename: ${filename}`)
      }

      // Create a download link for the blob
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()

      // Clean up
      setTimeout(() => {
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      }, 100)
    } catch (error) {
      logger.error("Error downloading file", error as Error)
      // Error toast is handled by the mutation hook
    }
  }

  const downloadLabel = t("analysispanel.fileviewer.download")
  // Include the file name in the accessible name when available so screen-reader
  // users can tell repeated download buttons apart. An explicit tooltip wins.
  const accessibleName = tooltip ?? (title ? `${downloadLabel} ${title}` : downloadLabel)

  return (
    <Tooltip>
      <TooltipTrigger
        aria-label={accessibleName}
        className={cn(
          buttonVariants({
            size: "icon",
            variant: "icon",
          })
        )}
        onClick={async (e) => await handleDownload(e)}
      >
        <FileDown className="size-6" />
      </TooltipTrigger>
      <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
        <p>{tooltip ?? t("analysispanel.fileviewer.download")}</p>
      </TooltipContent>
    </Tooltip>
  )
}
